import { DeliveryAddress } from 'state/delivery';
import { LocalDateTime } from 'js-joda';
import { PurchaseDetails } from 'components/business/receipts/PurchaseDetails';
import { consumer, core, safeEnumCompare } from '@tock/coretypes';
import { hasAnyMoniesInvolved } from 'utils/purchase';
import { isThirdPartyDeliveryVariety } from '@tock/shared/utils/offerings/varietyQueries';
import { mapOptionOrders } from '@tock/shared/utils/option';
import GratuityInput from 'components/business/gratuity/GratuityInput';
import ItemizedReceipt from 'components/business/receipts/ItemizedReceipt';
import ReceiptLine from 'components/business/receipts/ReceiptLine';
import _ from 'lodash';
import protos from 'utils/protos';

const { ExtraChargeDetails } = protos;

interface Props {
  alwaysShowAmountDue?: boolean;
  business: consumer.BusinessConfig | core.IBusinessPublic;
  combinedTotal?: number;
  dateTime: LocalDateTime;
  deliveryAddress?: DeliveryAddress;
  diner: core.Patron;
  extraChargeDetails?: core.IExtraChargeDetails;
  isAuthenticated?: boolean;
  isEvent: boolean;
  isLastExperience: boolean;
  isTableSideOrderPayment?: boolean;
  onGratuityChange?(newGratuityRate: number): void;
  onSwitchAccounts?(): void;
  owner: core.Patron;
  purchase: core.PurchaseOrder;
  showTockFeeIfRequired?: boolean;
  ticketType: core.TicketType;
  totalPrice: number;
}

const PurchaseOrderSummary: React.FC<Props> = ({
  alwaysShowAmountDue,
  business,
  combinedTotal,
  dateTime,
  deliveryAddress,
  diner,
  extraChargeDetails,
  isAuthenticated,
  isEvent,
  isLastExperience,
  isTableSideOrderPayment,
  onGratuityChange,
  onSwitchAccounts,
  owner,
  purchase,
  showTockFeeIfRequired,
  ticketType,
}) => {
  const options = mapOptionOrders(purchase);

  const moniesInvolved = hasAnyMoniesInvolved(purchase);

  const partySize = purchase?.ticketCount;

  const isGratuity =
    !_.isNil(extraChargeDetails) &&
    safeEnumCompare(
      extraChargeDetails.extraChargeType,
      ExtraChargeDetails.ExtraChargeType,
      ExtraChargeDetails.ExtraChargeType.GRATUITY
    ) &&
    onGratuityChange &&
    purchase.totalPrice > 0;

  const inVisitEdit = !!purchase.visitEditToken;
  return (
    <PurchaseDetails
      business={business}
      dateTime={dateTime}
      deliveryAddress={deliveryAddress}
      diner={diner}
      isAuthenticated={isAuthenticated}
      isEvent={isEvent}
      onSwitchAccounts={onSwitchAccounts}
      owner={owner}
      partySize={partySize}
      ticketTypeName={purchase.ticketTypeName}
      ticketType={ticketType}
      inVisitEdit={inVisitEdit}
      seatingAreaName={purchase.seatingAreaName}
    >
      {moniesInvolved ? (
        <ItemizedReceipt
          alwaysShowAmountDue={alwaysShowAmountDue}
          business={business}
          compAmount={purchase.compAmount}
          customCharge={purchase.customChargeOrder as core.CustomChargeOrder[]}
          deliveryServiceProvider={ticketType.deliveryServiceProvider}
          extraChargeDetails={extraChargeDetails as core.ExtraChargeDetails}
          options={options}
          purchase={purchase}
          partySize={partySize}
          purchaseFees={purchase.fee}
          title="Order Summary"
          showTockFeeIfRequired={showTockFeeIfRequired}
          isVisitEdit={inVisitEdit}
          isGratuityForThirdPartyDelivery={isThirdPartyDeliveryVariety(ticketType)}
          ticketType={ticketType}
        />
      ) : null}
      {isGratuity &&
        (!purchase.visitEditToken || isTableSideOrderPayment) &&
        onGratuityChange &&
        !_.isNil(extraChargeDetails) && (
          <GratuityInput
            deliveryServiceProvider={ticketType.deliveryServiceProvider}
            extraChargeDetails={extraChargeDetails as core.ExtraChargeDetails}
            purchase={purchase}
            onGratuityChange={onGratuityChange}
          />
        )}
      {isLastExperience && combinedTotal && (
        <div>
          <ReceiptLine
            amount={combinedTotal}
            className="Receipt-container--combinedTotal"
            bold
            lineItem="Combined Total"
          />
        </div>
      )}
    </PurchaseDetails>
  );
};

export default PurchaseOrderSummary;
